import { supabase } from '@/integrations/supabase/client';
import { Chat as AppChat, Message as AppMessage, Project as AppProject } from '@/types';

// ADD: Global kill switch helpers
const LOCAL_BLOCK_KEY = 'os_ai_block';

export function isOSAIBlocked(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(LOCAL_BLOCK_KEY) === '1';
}

export function setOSAIBlocked(blocked: boolean) {
  if (typeof window === 'undefined') return;
  if (blocked) {
    localStorage.setItem(LOCAL_BLOCK_KEY, '1');
  } else {
    localStorage.removeItem(LOCAL_BLOCK_KEY);
  }
  // Notify listeners in the UI
  window.dispatchEvent(new CustomEvent('os:ai-block-changed', { detail: { blocked } }));
}

type OSFileRow = {
  id: string;
  user_id: string;
  file_path: string;
  file_name: string;
  content: string | null;
  content_type: string | null;
  size_bytes: number | null;
  is_directory: boolean | null;
  parent_path: string | null;
};

const joinPath = (...parts: string[]) => {
  const joined = parts
    .filter(Boolean)
    .map((p) => p.replace(/\\/g, '/').replace(/^\/+|\/+$/g, ''))
    .join('/');
  return '/' + joined;
};

const parentOf = (path: string) => {
  const norm = path.replace(/\\/g, '/').replace(/\/+$/g, '');
  const idx = norm.lastIndexOf('/');
  if (idx <= 0) return '/';
  return norm.slice(0, idx);
};

const baseName = (path: string) => {
  const norm = path.replace(/\\/g, '/').replace(/\/+$/g, '');
  const idx = norm.lastIndexOf('/');
  return idx >= 0 ? norm.slice(idx + 1) : norm;
};

const sanitizeName = (name: string) =>
  name
    .trim()
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 100);

const shortId = (id: string) => id.substring(0, 8);

async function getCurrentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user?.id ?? null;
}

async function getFileByPath(userId: string, filePath: string) {
  const { data, error } = await supabase
    .from('os_user_files')
    .select('*')
    .eq('user_id', userId)
    .eq('file_path', filePath)
    .maybeSingle<OSFileRow>();
  if (error) throw error;
  return data;
}

async function ensureDir(userId: string, dirPath: string): Promise<OSFileRow> {
  const p = dirPath.endsWith('/') ? dirPath.slice(0, -1) : dirPath;
  const existing = await getFileByPath(userId, p);
  if (existing) return existing;

  const parent_path = parentOf(p);
  const file_name = baseName(p);
  const { data, error } = await supabase
    .from('os_user_files')
    .insert({
      user_id: userId,
      file_path: p,
      file_name,
      content: null,
      content_type: null,
      size_bytes: 0,
      is_directory: true,
      parent_path,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as OSFileRow;
}

async function ensurePathChain(userId: string, fullDirPath: string) {
  const parts = fullDirPath.split('/').filter(Boolean);
  let current = '/';
  for (const seg of parts) {
    current = current === '/' ? `/${seg}` : `${current}/${seg}`;
    // eslint-disable-next-line no-await-in-loop
    await ensureDir(userId, current);
  }
}

async function writeFile(
  userId: string,
  filePath: string,
  content: string,
  contentType = 'text/plain'
): Promise<OSFileRow> {
  const dir = parentOf(filePath);
  await ensurePathChain(userId, dir);

  const existing = await getFileByPath(userId, filePath);
  const file_name = baseName(filePath);

  if (existing) {
    const { data, error } = await supabase
      .from('os_user_files')
      .update({
        content,
        content_type: contentType,
        size_bytes: content.length,
        parent_path: dir,
        is_directory: false,
      })
      .eq('id', existing.id)
      .select('*')
      .single();
    if (error) throw error;
    return data as OSFileRow;
  }

  const { data, error } = await supabase
    .from('os_user_files')
    .insert({
      user_id: userId,
      file_path: filePath,
      file_name,
      content,
      content_type: contentType,
      size_bytes: content.length,
      is_directory: false,
      parent_path: dir,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as OSFileRow;
}

async function deleteFile(userId: string, filePath: string) {
  const existing = await getFileByPath(userId, filePath);
  if (!existing) return;
  const { error } = await supabase.from('os_user_files').delete().eq('id', existing.id);
  if (error) throw error;
}

// Paths
function getChatFolderPath(chatId: string, chatTitle: string) {
  const safe = sanitizeName(chatTitle.length ? chatTitle : 'Chat');
  return joinPath('Documents', 'Chats', `${safe}-${shortId(chatId)}`);
}

function getProjectFolderPath(projectId: string, projectName: string) {
  const safe = sanitizeName(projectName.length ? projectName : 'Project');
  return joinPath('Documents', 'Projects', `${safe}-${shortId(projectId)}`);
}

// Markers
function accessMarkerPath(folderPath: string) {
  return joinPath(folderPath, '.os-access.json');
}

function metaPath(folderPath: string) {
  return joinPath(folderPath, 'meta.json');
}

function conversationPath(folderPath: string) {
  return joinPath(folderPath, 'conversation.md');
}

// Public API: Chat
export async function isChatOSAccessEnabled(chatId: string, chatTitle: string) {
  // Respect global kill switch
  if (isOSAIBlocked()) return false;
  const userId = await getCurrentUserId();
  if (!userId) return false;
  const folder = getChatFolderPath(chatId, chatTitle);
  const marker = await getFileByPath(userId, accessMarkerPath(folder));
  return !!marker;
}

export async function enableChatOSAccess(chat: Pick<AppChat, 'id' | 'title'>, messages?: AppMessage[]) {
  // Block when kill switch is active
  if (isOSAIBlocked()) throw new Error('OS AI access is globally disabled.');
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const folder = getChatFolderPath(chat.id, chat.title);
  await ensurePathChain(userId, folder);

  const meta = {
    type: 'chat',
    id: chat.id,
    title: chat.title,
    enabledAt: new Date().toISOString(),
  };
  await writeFile(userId, metaPath(folder), JSON.stringify(meta, null, 2), 'application/json');

  const transcript =
    (messages && messages.length
      ? messages
          .map((m) => {
            const sender = m.role === 'user' ? 'You' : 'AI';
            return `${sender}: ${m.content}`;
          })
          .join('\n\n')
      : `# Conversation\n\n(Empty)\n`) + '\n';
  await writeFile(userId, conversationPath(folder), transcript, 'text/markdown');

  await writeFile(userId, accessMarkerPath(folder), JSON.stringify({ enabled: true, at: new Date().toISOString() }), 'application/json');

  return folder;
}

export async function disableChatOSAccess(chat: Pick<AppChat, 'id' | 'title'>) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');
  const folder = getChatFolderPath(chat.id, chat.title);
  await deleteFile(userId, accessMarkerPath(folder));
}

export async function appendChatMessageIfEnabled(chatId: string, chatTitle: string, message: AppMessage) {
  // If globally blocked, do nothing (silently prevent OS write)
  if (isOSAIBlocked()) return;

  const userId = await getCurrentUserId();
  if (!userId) return;

  const folder = getChatFolderPath(chatId, chatTitle);
  const marker = await getFileByPath(userId, accessMarkerPath(folder));
  if (!marker) return; // not enabled

  const convPath = conversationPath(folder);
  const existing = await getFileByPath(userId, convPath);
  const line = `${message.role === 'user' ? 'You' : 'AI'}: ${message.content}\n\n`;

  if (existing?.content) {
    await writeFile(userId, convPath, existing.content + line, 'text/markdown');
  } else {
    await writeFile(userId, convPath, line, 'text/markdown');
  }
}

// Public API: Project
export async function isProjectOSAccessEnabled(projectId: string, projectName: string) {
  if (isOSAIBlocked()) return false;
  const userId = await getCurrentUserId();
  if (!userId) return false;
  const folder = getProjectFolderPath(projectId, projectName);
  const marker = await getFileByPath(userId, accessMarkerPath(folder));
  return !!marker;
}

export async function enableProjectOSAccess(project: Pick<AppProject, 'id' | 'name' | 'description'>) {
  if (isOSAIBlocked()) throw new Error('OS AI access is globally disabled.');
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');

  const folder = getProjectFolderPath(project.id, project.name);
  await ensurePathChain(userId, folder);

  const meta = {
    type: 'project',
    id: project.id,
    name: project.name,
    description: project.description,
    enabledAt: new Date().toISOString(),
  };
  await writeFile(userId, metaPath(folder), JSON.stringify(meta, null, 2), 'application/json');

  const readme = `# ${project.name}\n\n${project.description || 'No description.'}\n`;
  await writeFile(userId, joinPath(folder, 'README.md'), readme, 'text/markdown');

  await writeFile(userId, accessMarkerPath(folder), JSON.stringify({ enabled: true, at: new Date().toISOString() }), 'application/json');

  return folder;
}

export async function disableProjectOSAccess(project: Pick<AppProject, 'id' | 'name'>) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('Not authenticated');
  const folder = getProjectFolderPath(project.id, project.name);
  await deleteFile(userId, accessMarkerPath(folder));
}